import React, { useEffect, useState } from "react";
import { useApp } from "context";
import { Content, Custom, Form, Progress } from "components";
import { LogoBrand } from "assets/images";
import { Box, Typography } from "@mui/material";
import { Enums, Routes } from "utils";
import { IAppointment, IPatient } from "interfaces";
import { useNavigate } from "react-router-dom";

type Props = {};

export const Consent = (props: Props) => {
  const navigate = useNavigate();
  const { setUser, service, t, theme, user } = useApp();
  const [patient, setPatient] = useState<IPatient>({} as IPatient);
  const [step, setStep] = useState<number>(Enums.EnumConsent.None);

  useEffect(() => {
    const fetchData = async () => {
      handleNextStep();

      if (step === Enums.EnumConsent.Profile) {
        const response = await service.patient.find();
        setPatient(response);
      }
    };
    fetchData();
  }, [step]);

  const handleStepper = async () => {
    switch (step) {
      case Enums.EnumConsent.TermsAndConditions: {
        setUser({ agreed: true });
        await service.patient.update({ agreed: true });
        break;
      }
      case Enums.EnumConsent.Profile: {
        setUser({ completed: true });
        break;
      }
      case Enums.EnumConsent.ConsentForm: {
        setUser({ appointments: [] });
        if (user.appointments) {
          user.appointments.forEach(async (appointment: IAppointment) => {
            await service.appointment.updateConsent(
              appointment.appointmentId,
              true
            );
          });
        }
        break;
      }
    }

    handleNextStep();
  };

  const handleNextStep = () => {
    if (!user.agreed) {
      // the user has not accepted the terms and conditions
      setStep(Enums.EnumConsent.TermsAndConditions);
    } else {
      // the user has accepted the terms and conditions
      if (user.appointments && user.appointments.length > 0) {
        // the user has checked in at the clinic
        const consentPending =
          user.appointments.filter((x) => !x.consented).length > 0;
        if (consentPending) {
          // the user has to agree to the consent form
          setStep(Enums.EnumConsent.ConsentForm);
        } else {
          setStep(Enums.EnumConsent.Profile);
        }
      } else {
        setStep(Enums.EnumConsent.Profile);
      }
    }
  };

  const handleSkip = async () => navigate(Routes.patient.home);

  const handleDisagree = async () => {
    await service.auth.signout(user.wq ?? "");
    // it should always clean the session no matter the status of
    // the response to prevent the user to be locked in the session.
    setUser(null);
    navigate(Routes.auth.signin);
  };

  return (
    <div className="qh-feedback">
      <div className="qh-wrapper">
        {step !== Enums.EnumConsent.None ? (
          <>
            <LogoBrand />
            <div
              className="qh-header"
              style={{ color: theme.palette.font.color }}
            >
              {/* <div className="title">{t.title.auth.welcome}</div> */}
              <div className="description">
                {step === Enums.EnumConsent.ConsentForm
                  ? t.message.auth.consent_agreement
                  : step === Enums.EnumConsent.Profile
                  ? t.message.auth.profile_intake
                  : t.message.auth.tac_agreement}
              </div>
            </div>
            <Box className="qh-content" sx={{ marginTop: theme.margin.md }}>
              {step === Enums.EnumConsent.Profile ? (
                <Form.Profile
                  isClinic={false}
                  requiredOnly={true}
                  patient={patient}
                  onSubmit={handleSkip}
                />
              ) : (
                <Box sx={{ height: 300, overflow: "auto" }}>
                  {step === Enums.EnumConsent.ConsentForm ? (
                    <Content.ConsentForm />
                  ) : (
                    <Content.TermsAndConditions />
                  )}
                </Box>
              )}
            </Box>
            <Box className="qh-footer" sx={{ marginTop: theme.margin.md }}>
              {step === Enums.EnumConsent.Profile && (
                <Typography
                  sx={{
                    fontSize: theme.font.sm,
                    marginBottom: theme.margin.md,
                  }}
                >
                  {t.message.auth.profile_footnote}
                </Typography>
              )}
              <div className="footnote" style={{ display: "flex" }}>
                {step !== Enums.EnumConsent.Profile ? (
                  <>
                    <Custom.Button
                      tabIndex={-1}
                      variant="outlined"
                      onClick={handleDisagree}
                      sx={{ marginRight: "auto" }}
                    >
                      {t.action.disagree}
                    </Custom.Button>
                    <Custom.Button onClick={handleStepper}>
                      {t.action.agree}
                    </Custom.Button>
                  </>
                ) : (
                  <Custom.Button
                    tabIndex={-1}
                    variant="outlined"
                    onClick={handleSkip}
                    sx={{ marginRight: "auto" }}
                  >
                    {t.action.skip}
                  </Custom.Button>
                )}
              </div>
            </Box>
          </>
        ) : (
          <Progress.PageProcess />
        )}
      </div>
    </div>
  );
};
